import { map } from 'lodash/fp';

import { Article } from '../../types/__codegen__/sanity/graphql';
import { ARTICLE_HUB_FEATURE, ARTICLE_HUB_FILTER_BAR_FEATURE } from '../../ahm-constants/ui';
import { ITEMS_PER_PAGE } from '../pages-hub/constants';
import { URLS } from '../../models/url/constants';
import { useFeatureIsOn } from '../../../../utils/growth-book/hooks';
import ArticleTile from '../../components/sections/articles-section/components/article-tile';
import FeaturedArticle from './components/featured-article';
import getShouldSkipClientSideRequest from '../../utils/get-should-skip-client-side-request';
import PagesHub from '../pages-hub';
import RedirectAdapter from '../../utils/redirect-adapter';
import TypeFilter from './components/type-filter';
import useGetLatestArticlesWithFeatured from '../blog-detail/hooks/use-get-latest-articles-with-featured';
import useIsAfterHydration from '../../hooks/use-is-after-hydration';

type ArticleHubProps = {
  articles: Article[] | null;
  currentPage: number;
  featuredArticle?: Article | null;
  totalArticles: number;
  insuranceType?: string | null;
};

const ArticleHub = ({ articles, currentPage, featuredArticle, totalArticles, insuranceType }: ArticleHubProps) => {
  const isFeatureEnabled = useFeatureIsOn(ARTICLE_HUB_FEATURE);
  const isFilterBarEnabled = useFeatureIsOn(ARTICLE_HUB_FILTER_BAR_FEATURE);
  const isAfterHydration = useIsAfterHydration();

  const shouldSkip = getShouldSkipClientSideRequest();
  const defaultOptions = { limit: ITEMS_PER_PAGE, offset: (currentPage - 1) * ITEMS_PER_PAGE };
  const {
    articlesData: clientData,
    error,
    featuredArticleData: featuredArticleClientData,
    isLoading,
    totalArticles: clientTotalArticles,
  } = useGetLatestArticlesWithFeatured({
    options: {
      skip: shouldSkip,
      variables: insuranceType ? { ...defaultOptions, type: insuranceType } : defaultOptions,
    },
  });

  if (isAfterHydration && !isFeatureEnabled) {
    return <RedirectAdapter to={URLS.SALES.ERROR_404} />;
  }

  const articlesToDisplay = shouldSkip ? articles : clientData;
  const featuredArticleToDisplay = shouldSkip ? featuredArticle : featuredArticleClientData?.[0]?.item;
  const totalArticlesToDisplay = shouldSkip ? totalArticles : clientTotalArticles;

  if (!isLoading && (error || !articlesToDisplay)) {
    return <RedirectAdapter to={URLS.SALES.ERROR_500} />;
  }

  const hubProps = {
    currentPage,
    featuredItem: featuredArticleToDisplay ? <FeaturedArticle featuredArticle={featuredArticleToDisplay} /> : undefined,
    filterBar: isFilterBarEnabled ? <TypeFilter /> : undefined,
    isLoading,
    itemsToDisplay: articlesToDisplay,
    title: 'ahm’s article hub',
    totalOfItems: totalArticlesToDisplay,
  };

  return (
    <PagesHub {...hubProps}>
      {map(
        (article) => (
          <ArticleTile item={article} key={article.name} />
        ),
        articlesToDisplay,
      )}
    </PagesHub>
  );
};

export default ArticleHub;
